import { z } from 'zod'

/**
 * The ready-made scopes a client can pick before adjusting anything.
 *
 * Mirrors the `ProposalPlan` enum in api/prisma/schema.prisma.
 *
 * A plan is a preset, not a second way of pricing. Each one is expressed as
 * the trees it leaves out, written into the same selection the adjustments
 * use, so the browser and the acceptance API price it with the one shared
 * helper and cannot disagree about what was agreed.
 *
 * Named by what it does, never "package": a board reading "Hazards first"
 * knows what it is buying without a legend.
 */
export const PROPOSAL_PLANS = [
  'ALL',
  'HAZARDS',
  'PHASED',
  'BUDGET',
  'CORE',
] as const

export const proposalPlanSchema = z.enum(PROPOSAL_PLANS)

export type ProposalPlan = z.infer<typeof proposalPlanSchema>

/**
 * The plans a proposal offers today, in the order the cards show them.
 *
 * "Over two budget years" (PHASED) and "Fit to a budget" (BUDGET) were
 * withdrawn on 2026-10-09: a board with a budget adjusts what is included
 * instead. Both stay in PROPOSAL_PLANS, because proposals accepted before
 * then recorded them, and the CRM still names what those clients chose.
 */
export const OFFERED_PROPOSAL_PLANS = [
  'HAZARDS',
  'CORE',
  'ALL',
] as const satisfies readonly ProposalPlan[]

export type OfferedProposalPlan = (typeof OFFERED_PROPOSAL_PLANS)[number]

export function isOfferedPlan(plan: ProposalPlan): plan is OfferedProposalPlan {
  return OFFERED_PROPOSAL_PLANS.some((offered) => offered === plan)
}

/**
 * Labels when nothing is marked optional, which is also how the CRM names an
 * accepted plan.
 */
export const PROPOSAL_PLAN_LABELS: Record<ProposalPlan, string> = {
  ALL: 'Everything recommended',
  HAZARDS: 'Hazards first',
  PHASED: 'Over two budget years',
  BUDGET: 'Fit to a budget',
  CORE: 'Everything recommended, without the optional work',
}

/**
 * A plan's name on a proposal.
 *
 * When the arborist marked some work optional, "everything" splits in two:
 * CORE is the recommendation and ALL adds the optional work. Without optional
 * work there is no CORE, and ALL is simply everything recommended.
 */
export function planLabel(plan: ProposalPlan, hasOptional: boolean): string {
  if (!hasOptional) return PROPOSAL_PLAN_LABELS[plan]
  if (plan === 'CORE') return 'Everything recommended'
  if (plan === 'ALL') return 'Everything, with the optional work'
  return PROPOSAL_PLAN_LABELS[plan]
}
